package fr.manyverse.app.infrastructure.datastore

import kotlinx.browser.localStorage

class LocalStorageUserPreferencesDataStore : UserPreferencesDataStore {

    override suspend fun saveTermsAccepted(userId: String) {
        localStorage.setItem(termsKey(userId), "true")
    }

    override suspend fun isTermsAccepted(userId: String): Boolean =
        localStorage.getItem(termsKey(userId)) == "true"

    override suspend fun saveThemePreference(isDark: Boolean?) {
        if (isDark == null) localStorage.removeItem(THEME_KEY)
        else localStorage.setItem(THEME_KEY, isDark.toString())
    }

    override suspend fun getThemePreference(): Boolean? =
        localStorage.getItem(THEME_KEY)?.let { it == "true" }

    override suspend fun saveDismissedAnnouncements(
        userId: String,
        tableId: String,
        eventIds: Set<String>
    ) {
        if (eventIds.isEmpty()) {
            localStorage.removeItem(dismissedAnnouncementsKey(userId, tableId))
        } else {
            localStorage.setItem(
                dismissedAnnouncementsKey(userId, tableId),
                eventIds.joinToString(SEPARATOR)
            )
        }
    }

    override suspend fun getDismissedAnnouncements(
        userId: String,
        tableId: String
    ): Set<String> {
        val raw = localStorage.getItem(dismissedAnnouncementsKey(userId, tableId)) ?: return emptySet()
        if (raw.isEmpty()) return emptySet()
        return raw.split(SEPARATOR).toSet()
    }

    override suspend fun saveLastSeenAt(userId: String, tableId: String, epochMillis: Long) {
        localStorage.setItem(lastSeenAtKey(userId, tableId), epochMillis.toString())
    }

    override suspend fun getLastSeenAt(userId: String, tableId: String): Long? =
        localStorage.getItem(lastSeenAtKey(userId, tableId))?.toLongOrNull()

    override suspend fun saveDiceConfig(
        userId: String,
        tableId: String,
        dieType: String,
        count: Int,
        modifier: Int
    ) {
        localStorage.setItem(diceConfigKey(userId, tableId), "$dieType|$count|$modifier")
    }

    override suspend fun getDiceConfig(userId: String, tableId: String): StoredDiceConfig? {
        val raw = localStorage.getItem(diceConfigKey(userId, tableId)) ?: return null
        val parts = raw.split("|")
        if (parts.size != 3) return null
        val count = parts[1].toIntOrNull() ?: return null
        val modifier = parts[2].toIntOrNull() ?: return null
        return StoredDiceConfig(parts[0], count, modifier)
    }

    override suspend fun saveDiceTrayColor(colorId: String) {
        localStorage.setItem(DICE_TRAY_KEY, colorId)
    }

    // substringBefore("|") tolère l'ancien format "color|shape".
    override suspend fun getDiceTrayColor(): String? =
        localStorage.getItem(DICE_TRAY_KEY)?.substringBefore("|")

    override suspend fun saveDiceColor(colorId: String) {
        localStorage.setItem(DICE_COLOR_KEY, colorId)
    }

    override suspend fun getDiceColor(): String? =
        localStorage.getItem(DICE_COLOR_KEY)

    override suspend fun saveDiceOneFace(faceId: String) {
        localStorage.setItem(DICE_ONE_FACE_KEY, faceId)
    }

    override suspend fun getDiceOneFace(): String? =
        localStorage.getItem(DICE_ONE_FACE_KEY)

    private fun termsKey(userId: String) = "terms_accepted:$userId"
    private fun dismissedAnnouncementsKey(userId: String, tableId: String) =
        "dismissed_announcements:$userId:$tableId"
    private fun lastSeenAtKey(userId: String, tableId: String) =
        "last_seen_at:$userId:$tableId"
    private fun diceConfigKey(userId: String, tableId: String) =
        "dice_config:$userId:$tableId"

    companion object {
        private const val THEME_KEY = "theme_dark"
        private const val DICE_TRAY_KEY = "dice_tray"
        private const val DICE_COLOR_KEY = "dice_color"
        private const val DICE_ONE_FACE_KEY = "dice_one_face"
        private const val SEPARATOR = "|"
    }
}

actual fun createUserPreferencesDataStore(): UserPreferencesDataStore =
    LocalStorageUserPreferencesDataStore()
